import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { colors } from '../styles/colors';
import { Download } from 'lucide-react';
import { useFillout } from '../hooks/useFillout';

const CapitalBarriersArticlePage = () => {
  const [isVisible, setIsVisible] = useState(false);
  const { openFilloutPopup } = useFillout();

  useEffect(() => {
    setIsVisible(true);
    window.scrollTo(0, 0);
    // Intentional: mount-only effect; deps intentionally omitted
  }, []);

  // Get today's date formatted
  const today = new Date();
  const formattedDate = today.toLocaleDateString('en-US', { 
    day: 'numeric', 
    month: 'long', 
    year: 'numeric' 
  });

  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.black }}>
      {/* Fillout popup is now managed globally via FilloutProvider */}

      {/* SEO & Open Graph Metadata */}
      <Helmet>
        <title>Overcoming Capital Access Barriers in Mining | Arizore</title>
        <meta name="description" content="How structured reserve infrastructure turns verified mining assets into transparent, institution-ready collateral for capital access." />
        <link rel="canonical" href="https://arizore.io/reserve-room/written/overcoming-capital-access-barriers" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Overcoming Capital Access Barriers in Mining" />
        <meta property="og:description" content="How structured reserve infrastructure turns verified mining assets into transparent, institution-ready collateral for capital access." />
        <meta property="og:image" content="https://arizore.io/capital-barriers-hero.png" />
        <meta property="og:url" content="https://arizore.io/reserve-room/written/overcoming-capital-access-barriers" />
        <meta property="og:type" content="article" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Overcoming Capital Access Barriers in Mining" />
        <meta name="twitter:description" content="How structured reserve infrastructure turns verified mining assets into transparent, institution-ready collateral for capital access." />
        <meta name="twitter:image" content="https://arizore.io/capital-barriers-hero.png" />
      </Helmet>

      {/* Main Content */}
      <article className="pt-24 sm:pt-28 pb-16 sm:pb-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          
          {/* Breadcrumb */}
          <div 
            className={`mb-4 transition-all duration-1000 transform ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            <span 
              className="text-xs sm:text-sm font-medium uppercase tracking-wider"
              style={{ color: colors.teal }}
            >
              Written Content
            </span>
          </div>

          {/* Page Title */}
          <h1 
            className={`text-3xl sm:text-4xl md:text-5xl font-bold mb-4 leading-tight transition-all duration-1000 transform ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
            style={{ color: colors.white, transitionDelay: '50ms' }}
          >
            Overcoming Capital Access Barriers in Mining
          </h1>

          {/* Subtitle */}
          <p 
            className={`text-lg sm:text-xl mb-4 transition-all duration-1000 transform ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
            style={{ color: colors.white, opacity: 0.7, transitionDelay: '75ms' }}
          >
            Turning Verified Reserves into Usable Collateral
          </p>

          {/* Publish Date & Author */}
          <div 
            className={`mb-8 sm:mb-10 transition-all duration-1000 transform ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
            style={{ transitionDelay: '100ms' }}
          >
            <span 
              className="text-sm"
              style={{ color: colors.white, opacity: 0.5 }}
            >
              {formattedDate} · The Arizore Team
            </span>
          </div>

          {/* Hero Image */}
          <div 
            className={`mb-8 sm:mb-10 transition-all duration-1000 transform ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
            style={{ transitionDelay: '150ms' }}
          >
            <img 
              src="/capital-barriers-hero.png" 
              alt="Overcoming Capital Access Barriers in Mining" 
              className="w-full rounded-lg"
            />
          </div>

          {/* Article Content */}
          <div 
            className={`prose prose-lg max-w-none transition-all duration-1000 transform ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
            style={{ transitionDelay: '200ms' }}
          >
            {/* Opening */}
            <p style={{ color: colors.white, opacity: 0.85, marginBottom: '1.5rem', lineHeight: '1.8' }}>
              Many mining companies hold verified reserves that remain locked behind traditional financing barriers. You face long timelines and capital constraints that limit your ability to use these assets effectively. This post explains how real-world asset (RWA) reserve infrastructure can turn verified reserves into institutional-grade collateral, supporting reserve-backed financing frameworks while enhancing transparency and governance for all parties involved.
            </p>

            <p style={{ color: colors.white, opacity: 0.85, marginBottom: '1.5rem', lineHeight: '1.8' }}>
              In simple terms, it means pledging via a forward contract into a structured digital representation. A verifiable container that makes it visible, legible, and usable within financial systems.
            </p>

            {/* Structural Barriers */}
            <h2 style={{ color: colors.white, fontSize: '1.5rem', fontWeight: '600', marginTop: '2.5rem', marginBottom: '1rem' }}>
              Structural Barriers in Mining Finance
            </h2>
            <p style={{ color: colors.white, opacity: 0.85, marginBottom: '1.5rem', lineHeight: '1.8' }}>
              In the mining sector, traditional finance options are limited. Banks often require extensive documentation and equity, slowing down your progress. Private equity entails long due diligence periods and significant ownership dilution. High-net-worth individuals might offer quicker solutions but at a high cost, demanding control or personal guarantees. These constraints slow growth and limit financial flexibility.
            </p>
            <p style={{ color: colors.white, opacity: 0.85, marginBottom: '1.5rem', lineHeight: '1.8' }}>
              A closer look reveals that your verified reserves hold hidden potential. The challenge is not the proven mineral itself—it's that the financial system cannot properly recognise or interact with it in its current form.
            </p>

            {/* RWA Reserve Infrastructure */}
            <h2 style={{ color: colors.white, fontSize: '1.5rem', fontWeight: '600', marginTop: '2.5rem', marginBottom: '1rem' }}>
              Real-World Asset Reserve Infrastructure
            </h2>
            <p style={{ color: colors.white, opacity: 0.85, marginBottom: '1.5rem', lineHeight: '1.8' }}>
              Real-world asset reserve infrastructure offers a new way forward. This system enables reserve-linked assets to participate within structured collateral frameworks without giving up ownership.
            </p>
            <p style={{ color: colors.white, opacity: 0.85, marginBottom: '1.5rem', lineHeight: '1.8' }}>
              Rather than changing the asset, what changes is its format. Your reserve is given a digital representation that can be verified, tracked, and understood by financial counterparties, effectively making it usable within structured finance environments.
            </p>
            <p style={{ color: colors.white, opacity: 0.85, marginBottom: '1.5rem', lineHeight: '1.8' }}>
              This approach is particularly relevant in modern finance, where transparency and asset-backed structures are crucial. It's time to consider how RWA reserve infrastructure can unlock your reserves' full potential.
            </p>

            {/* Verified Reserves as Financial Collateral */}
            <h2 style={{ color: colors.white, fontSize: '1.5rem', fontWeight: '600', marginTop: '2.5rem', marginBottom: '1rem' }}>
              Verified Reserves as Financial Collateral
            </h2>
            <p style={{ color: colors.white, opacity: 0.85, marginBottom: '1.5rem', lineHeight: '1.8' }}>
              Your verified reserves can become usable financial collateral, changing how you approach funding. Imagine integrating in-ground assets into a structured reserve framework designed for institutional collateral participation.
            </p>
            <p style={{ color: colors.white, opacity: 0.85, marginBottom: '1.5rem', lineHeight: '1.8' }}>
              In simple terms, your asset is placed into a digital container that proves its existence, ownership, and verified value. This representation allows lenders to treat it as credible collateral, without requiring physical delivery or ownership transfer.
            </p>
            <p style={{ color: colors.white, opacity: 0.85, marginBottom: '1.5rem', lineHeight: '1.8' }}>
              This shift redefines mining finance, allowing you to focus on growth rather than dilution or asset sales.
            </p>
            <p style={{ color: colors.white, opacity: 0.85, marginBottom: '1.5rem', lineHeight: '1.8' }}>
              The potential here is immense. With verified reserves, you have a powerful tool at your disposal. By embracing this approach, you can pave the way for a more efficient and sustainable financial strategy.
            </p>

            {/* Asset-Backed Collateral Structures */}
            <h2 style={{ color: colors.white, fontSize: '1.5rem', fontWeight: '600', marginTop: '2.5rem', marginBottom: '1rem' }}>
              Asset-Backed Collateral Structures
            </h2>
            <p style={{ color: colors.white, opacity: 0.85, marginBottom: '1.5rem', lineHeight: '1.8' }}>
              Asset-backed collateral structures offer a robust framework for your reserves. These structures provide a secure and transparent way to use your assets in financial transactions.
            </p>
            <p style={{ color: colors.white, opacity: 0.85, marginBottom: '1.5rem', lineHeight: '1.8' }}>
              At the core, this is about standardising how your asset is seen by the financial system. Through verified data, governance, and structured representation, your reserves become something institutions can underwrite, monitor, and rely on.
            </p>
            <p style={{ color: colors.white, opacity: 0.85, marginBottom: '1.5rem', lineHeight: '1.8' }}>
              By creating a documented and verifiable representation of your reserve, you ensure that your collateral is recognised and valued in the financial world. This approach aligns with the needs of modern finance, focusing on transparency and verifiability.
            </p>
            <p style={{ color: colors.white, opacity: 0.85, marginBottom: '1.5rem', lineHeight: '1.8' }}>
              Now, you can explore different ways to optimise your reserves. Asset-backed structures provide the flexibility to participate in reserve-backed infrastructure without transferring operational ownership.
            </p>

            {/* Reserve Transparency and Reporting */}
            <h2 style={{ color: colors.white, fontSize: '1.5rem', fontWeight: '600', marginTop: '2.5rem', marginBottom: '1rem' }}>
              Reserve Transparency and Reporting
            </h2>
            <p style={{ color: colors.white, opacity: 0.85, marginBottom: '1.5rem', lineHeight: '1.8' }}>
              Transparency in reserve reporting is essential. It builds trust with investors and stakeholders, ensuring that your reserves are accurately valued and documented.
            </p>
            <p style={{ color: colors.white, opacity: 0.85, marginBottom: '1.5rem', lineHeight: '1.8' }}>
              When your asset is digitally represented within a structured reserve system, its status, changes, and allocations can be tracked with far greater clarity. This visibility is what enables financial counterparties to engage with it confidently.
            </p>
            <p style={{ color: colors.white, opacity: 0.85, marginBottom: '1.5rem', lineHeight: '1.8' }}>
              By implementing clear reporting standards, you provide a comprehensive view of your assets. This approach aligns with the expectations of modern finance, emphasising accountability and openness.
            </p>
            <p style={{ color: colors.white, opacity: 0.85, marginBottom: '1.5rem', lineHeight: '1.8' }}>
              It's time to rethink how you report and manage your reserves. By adopting transparent practices, you can improve your financial standing and strengthen relationships with partners.
            </p>

            {/* Governance and Reserve Eligibility */}
            <h2 style={{ color: colors.white, fontSize: '1.5rem', fontWeight: '600', marginTop: '2.5rem', marginBottom: '1rem' }}>
              Governance and Reserve Eligibility
            </h2>
            <p style={{ color: colors.white, opacity: 0.85, marginBottom: '1.5rem', lineHeight: '1.8' }}>
              Strong governance structures are vital for reserve eligibility. By ensuring that your reserves meet established standards, you enhance your credibility in the market.
            </p>
            <p style={{ color: colors.white, opacity: 0.85, marginBottom: '1.5rem', lineHeight: '1.8' }}>
              This includes how the asset is verified, how it is represented, and how it is maintained within the reserve system. Clear separation between ownership, verification, and usage is what gives the collateral its institutional credibility.
            </p>
            <p style={{ color: colors.white, opacity: 0.85, marginBottom: '1.5rem', lineHeight: '1.8' }}>
              Governance is not just about meeting standards; it's about building confidence in your assets. By focusing on governance, you create a foundation for long-term financial success.
            </p>
            <p style={{ color: colors.white, opacity: 0.85, marginBottom: '1.5rem', lineHeight: '1.8' }}>
              This approach not only enhances your reputation but also opens up new opportunities for growth.
            </p>

            {/* Closing CTA */}
            <div 
              style={{ 
                backgroundColor: `${colors.white}05`, 
                border: `1px solid ${colors.white}15`,
                borderRadius: '8px',
                padding: '1.5rem',
                marginTop: '2.5rem',
                marginBottom: '1.5rem'
              }}
            >
              <p style={{ color: colors.white, opacity: 0.9, marginBottom: '0.5rem', lineHeight: '1.8' }}>
                <strong style={{ color: colors.teal }}>Explore how reserve-backed infrastructure frameworks may support institutional collateral participation.</strong>
              </p>
              <p style={{ color: colors.white, opacity: 0.7, lineHeight: '1.8', marginBottom: 0 }}>
                Contact us to learn more at{' '}
                <a 
                  href="https://www.arizore.io" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ color: colors.teal, textDecoration: 'underline' }}
                >
                  arizore.io
                </a>
              </p>
            </div>
          </div>

          {/* CTA Buttons */}
          <div 
            className={`mt-12 sm:mt-16 pt-8 sm:pt-10 border-t transition-all duration-1000 transform ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
            style={{ borderColor: `${colors.white}15`, transitionDelay: '300ms' }}
          >
            {/* PDF Download Button */}
            <a
              href="/overcoming-capital-barriers.pdf"
              download="Overcoming-Capital-Access-Barriers-Mining.pdf"
              className="inline-flex items-center gap-3 px-5 py-3 rounded-lg text-sm font-medium transition-all duration-200 w-full sm:w-auto justify-center sm:justify-start"
              style={{ 
                backgroundColor: `${colors.white}10`,
                color: colors.white,
                opacity: 0.9
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = `${colors.white}20`;
                e.currentTarget.style.opacity = '1';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = `${colors.white}10`;
                e.currentTarget.style.opacity = '0.9';
              }}
            >
              <Download className="w-4 h-4" />
              Download Full Article (PDF)
            </a>

            {/* Talk to the Team Button */}
            <button
              onClick={openFilloutPopup}
              data-testid="capital-barriers-talk-to-team-btn"
              className="inline-flex items-center gap-3 px-5 py-3 rounded-lg text-sm font-medium transition-all duration-200 w-full sm:w-auto justify-center sm:justify-start mt-4 sm:mt-0 sm:ml-4"
              style={{ 
                backgroundColor: 'transparent',
                color: colors.white,
                opacity: 0.7,
                border: `1px solid ${colors.white}30`
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = `${colors.white}10`;
                e.currentTarget.style.opacity = '0.9';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.opacity = '0.7';
              }}
            >
              Talk to the team
            </button>
          </div>

          {/* Disclaimer Block */}
          <div 
            className={`mt-12 sm:mt-16 pt-8 sm:pt-10 border-t transition-all duration-1000 transform ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
            style={{ borderColor: `${colors.white}10`, transitionDelay: '400ms' }}
          >
            <p 
              className="text-xs leading-relaxed"
              style={{ color: colors.white, opacity: 0.4 }}
            >
              This content is for informational purposes only and does not constitute financial advice or an offer to participate in any financial product or structure. Asset representations and reserve structures referenced are subject to verification, eligibility, and jurisdictional considerations.
            </p>
          </div>

        </div>
      </article>
    </div>
  );
};

export default CapitalBarriersArticlePage;
